// src/pages/EmailVerificationHandler.js
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db } from "../firebase";
import { applyActionCode, checkActionCode } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import "./Login.css";

export default function EmailVerificationHandler() {
  const [status, setStatus] = useState("verifying"); // verifying, success, error, expired
  const [errorMsg, setErrorMsg] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    async function handleVerification() {
      const mode = searchParams.get("mode");
      const oobCode = searchParams.get("oobCode");

      // Check if this is an email verification action
      if (mode !== "verifyEmail" || !oobCode) {
        setStatus("error");
        setErrorMsg("Invalid verification link.");
        return;
      }

      try {
        // Check if the code is valid
        await checkActionCode(auth, oobCode);

        // Apply the verification code
        await applyActionCode(auth, oobCode);

        // Get the current user
        const user = auth.currentUser;
        if (user) {
          // Reload user to get updated emailVerified status
          await user.reload();

          // Update Firestore
          try {
            await updateDoc(doc(db, "users", user.uid), {
              emailVerified: true,
            });
          } catch (error) {
            console.error("Error updating Firestore:", error);
          }
        }

        setStatus("success");

        // Start countdown
        const interval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              navigate("/dashboard", { replace: true });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      } catch (error) {
        console.error("Verification error:", error);

        if (error.code === "auth/invalid-action-code") {
          setStatus("expired");
          setErrorMsg("This verification link has expired or has already been used.");
        } else if (error.code === "auth/expired-action-code") {
          setStatus("expired");
          setErrorMsg("This verification link has expired. Please request a new one.");
        } else {
          setStatus("error");
          setErrorMsg("Unable to verify email. Please try again.");
        }
      }
    }

    handleVerification();
  }, [searchParams, navigate]);

  return (
    <div className="login-page">
      <div className="login-overlay"></div>

      <div className="login-container" style={{
        background: 'rgba(20, 20, 20, 0.95)',
        padding: '50px 40px',
        maxWidth: '550px'
      }}>
        {/* Verifying */}
        {status === "verifying" && (
          <>
            <div style={{
              fontSize: '72px',
              textAlign: 'center',
              marginBottom: '20px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              ⏳
            </div>
            <h2 style={{
              color: '#ff3366',
              fontSize: '2rem',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              Verifying Your Email...
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              fontSize: '1rem',
              margin: '0'
            }}>
              Please wait while we confirm your email address.
            </p>
          </>
        )}

        {/* Success */}
        {status === "success" && (
          <>
            <div style={{
              fontSize: '96px',
              textAlign: 'center',
              marginBottom: '20px',
              animation: 'bounceIn 0.6s ease-out'
            }}>
              ✅
            </div>
            <h2 style={{
              color: '#00ff88',
              fontSize: '2.2rem',
              textAlign: 'center',
              marginBottom: '20px',
              fontWeight: '700'
            }}>
              Email Verified!
            </h2>
            <p style={{
              color: '#fff',
              textAlign: 'center',
              fontSize: '1.2rem',
              margin: '0 0 30px 0',
              fontWeight: '600'
            }}>
              🎉 Success! Your account is now fully activated.
            </p>
            <p style={{
              color: 'rgba(255, 255, 255, 0.7)',
              textAlign: 'center',
              fontSize: '1rem',
              margin: '0 0 30px 0',
              lineHeight: '1.6'
            }}>
              You're all set to start solving cases on Craic on the Case!
            </p>

            <div style={{
              background: 'rgba(0, 255, 136, 0.1)',
              border: '1px solid rgba(0, 255, 136, 0.3)',
              borderRadius: '8px',
              padding: '20px',
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              <p style={{
                color: '#00ff88',
                fontSize: '1.1rem',
                margin: '0',
                fontWeight: '600'
              }}>
                Redirecting to dashboard in {countdown} seconds...
              </p>
            </div>

            <button
              onClick={() => navigate("/dashboard", { replace: true })}
              style={{
                width: '100%',
                padding: '16px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(255, 51, 102, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(255, 51, 102, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(255, 51, 102, 0.3)';
              }}
            >
              Go to Dashboard Now →
            </button>
          </>
        )}

        {/* Expired */}
        {status === "expired" && (
          <>
            <div style={{
              fontSize: '72px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              ⌛
            </div>
            <h2 style={{
              color: '#ff9966',
              fontSize: '2rem',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              Link Expired
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              fontSize: '1rem',
              margin: '0 0 30px 0',
              lineHeight: '1.6'
            }}>
              {errorMsg}
            </p>

            <button
              onClick={() => navigate("/verify-email", { replace: true })}
              style={{
                width: '100%',
                padding: '14px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '12px'
              }}
            >
              Request New Verification Link
            </button>

            <button
              onClick={() => navigate("/login", { replace: true })}
              style={{
                width: '100%',
                padding: '13px',
                background: 'transparent',
                color: '#ff3366',
                border: '2px solid #ff3366',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Back to Login
            </button>
          </>
        )}

        {/* Error */}
        {status === "error" && (
          <>
            <div style={{
              fontSize: '72px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              ❌
            </div>
            <h2 style={{
              color: '#ff3366',
              fontSize: '2rem',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              Verification Failed
            </h2>
            <p style={{
              color: 'rgba(255, 255, 255, 0.8)',
              textAlign: 'center',
              fontSize: '1rem',
              margin: '0 0 30px 0',
              lineHeight: '1.6'
            }}>
              {errorMsg}
            </p>

            <button
              onClick={() => navigate("/verify-email", { replace: true })}
              style={{
                width: '100%',
                padding: '14px',
                background: '#ff3366',
                border: 'none',
                borderRadius: '8px',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                marginBottom: '12px'
              }}
            >
              Try Again
            </button>

            <button
              onClick={() => navigate("/login", { replace: true })}
              style={{
                width: '100%',
                padding: '13px',
                background: 'transparent',
                color: '#ff3366',
                border: '2px solid #ff3366',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Back to Login
            </button>
          </>
        )}

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }

          @keyframes bounceIn {
            0% { opacity: 0; transform: scale(0.3); }
            50% { opacity: 1; transform: scale(1.1); }
            70% { transform: scale(0.9); }
            100% { transform: scale(1); }
          }
        `}</style>
      </div>
    </div>
  );
}